import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field, Query } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Base } from '@adornis/buildify/db/Base.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { WikiEntry } from './WikiEntry.js';

@Entity()
export class WikiSettings extends MongoEntity {
  static override _class = 'WikiSettings';
  static override _collectionName = 'wiki-settings';

  static override get allFields() {
    return selectionSet(() => this, 4);
  }

  static async getSettings(): Promise<WikiSettings> {
    const settings = await this.ensureWikiSettings()(WikiSettings.allFields);
    return settings;
  }

  @Field(() => Float, { default: v => v ?? 0.01 })
  searchMinScore: Maybe<number>;

  @validate(nonOptional())
  @Field(() => [String], { default: v => v ?? [] })
  sidebarItemIDs!: string[];

  @Field(() => [Base], { default: v => v ?? [] })
  pageTemplate!: Base[];

  @Query(() => WikiSettings)
  static ensureWikiSettings() {
    return async (gqlFields: BaseQLSelectionSet<WikiSettings>): Promise<WikiSettings> => {
      const globalSettingsIdentifier = 'global';
      const rawSettingsCollection = await getRawCollection<WikiSettings>(WikiSettings._collectionName);
      const settings = await rawSettingsCollection.findOne<WikiSettings>({ _id: globalSettingsIdentifier });
      if (settings) {
        const rawWikiEntryCollection = await getRawCollection<WikiEntry>(WikiEntry._collectionName);
        const validResults = await rawWikiEntryCollection
          .find<WikiEntry>({ _id: { $in: settings.sidebarItemIDs } }, { projection: { _id: 1 } })
          .toArray();
        const validResultIDs = validResults.map(res => res._id);
        settings.sidebarItemIDs = settings.sidebarItemIDs.filter(id => validResultIDs.includes(id));
        await rawSettingsCollection.updateOne(
          { _id: globalSettingsIdentifier },
          { $set: { sidebarItemIDs: settings.sidebarItemIDs } },
        );
        return settings;
      }
      const newSettings = new WikiSettings({ _id: globalSettingsIdentifier });
      newSettings._id = await newSettings.create();
      return newSettings;
    };
  }
}
